import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { IExposedFunctionality, IExposedRoute } from "../interfaces/access.interface";
import { ConnectionEventsService } from "./connection-events.service";

/**
 * Manages the current session's authentication tokens and preferences, persisted in
 * localStorage.
 */
@Injectable({
  providedIn: "root",
})
export class SessionService {
  private readonly accessTokenIndex = "access_token";
  private readonly refreshTokenIndex = "refresh_token";
  private readonly currentOrgIdIndex = "current_org";
  private readonly langIndex = "lang";
  private readonly functionalitiesIndex = "functionalities";
  private readonly routesIndex = "routes";

  public readonly DEFAULT_LANG = "en";

  private readonly loginStateChangedSubject = new Subject<void>();

  /**
   * In-memory copies of the stored functionalities and routes, so that permission checks (run on
   * every change detection) don't re-read and re-parse localStorage each time. `undefined` means
   * not read from localStorage yet; localStorage remains the persistence across page reloads.
   */
  private cachedFunctionalities: IExposedFunctionality[] | null | undefined = undefined;
  private cachedRoutes: IExposedRoute[] | null | undefined = undefined;

  /**
   * Emits whenever the login state changes (e.g. after a successful login or logout), so that
   * components already present in the DOM (such as the account menu, which is never re-instantiated
   * on navigation) can react and recompute state that depends on the current session.
   */
  public readonly onLoginStateChanged$: Observable<void> = this.loginStateChangedSubject.asObservable();

  constructor(private connectionEventsService: ConnectionEventsService) {
    // Another tab changed the stored functionalities or routes (e.g. logged out or switched
    // organization): drop the in-memory copies so they're read again from localStorage
    window.addEventListener("storage", (event: StorageEvent) => {
      if (event.key === null || event.key === this.functionalitiesIndex) {
        this.cachedFunctionalities = undefined;
      }
      if (event.key === null || event.key === this.routesIndex) {
        this.cachedRoutes = undefined;
      }
    });
    this.connectionEventsService.logout$.subscribe(() => {
      this.clear();
    });
    this.connectionEventsService.login$.subscribe(({ accessToken, refreshToken, organizationId, functionalities, routes, language }) => {
      if (accessToken) {
        this.accessToken = accessToken;
      }
      if (refreshToken) {
        this.refreshToken = refreshToken;
      }
      if (language) {
        this.userLang = language;
      }
      this.currentOrgId = organizationId;
      this.functionalities = functionalities;
      this.routes = routes;
      this.notifyLoginStateChanged();
    });
  }

  /**
   * Notifies subscribers that the login state has changed.
   */
  public notifyLoginStateChanged(): void {
    this.loginStateChangedSubject.next();
  }

  /**
   * Clears all stored session data (access token, refresh token, current organization, granted
   * functionalities and routes) and notifies subscribers that the login state has changed.
   */
  public clear(): void {
    localStorage.removeItem(this.accessTokenIndex);
    localStorage.removeItem(this.refreshTokenIndex);
    localStorage.removeItem(this.currentOrgIdIndex);
    localStorage.removeItem(this.functionalitiesIndex);
    localStorage.removeItem(this.routesIndex);
    this.cachedFunctionalities = null;
    this.cachedRoutes = null;
    this.notifyLoginStateChanged();
  }

  /**
   * Stores the access token.
   * @param token The access token to store.
   */
  public set accessToken(token: string) {
    localStorage.setItem(this.accessTokenIndex, token);
  }

  /**
   * Retrieves the stored access token.
   * @return The stored access token, or null if none is stored.
   */
  public get accessToken(): string | null {
    return localStorage.getItem(this.accessTokenIndex);
  }

  /**
   * Stores the refresh token.
   * @param token The refresh token to store.
   */
  public set refreshToken(token: string) {
    localStorage.setItem(this.refreshTokenIndex, token);
  }

  /**
   * Retrieves the stored refresh token.
   * @return The stored refresh token, or null if none is stored.
   */
  public get refreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenIndex);
  }

  /**
   * Determines whether a user is currently logged in.
   * @return True if an access token is stored, false otherwise.
   */
  public get isLoggedIn(): boolean {
    return this.accessToken !== null ? true : false;
  }

  /**
   * Stores the identifier of the organization the session is currently scoped to.
   * @param orgId Identifier of the organization.
   */
  public set currentOrgId(orgId: number) {
    localStorage.setItem(this.currentOrgIdIndex, JSON.stringify(orgId));
  }

  /**
   * Retrieves the identifier of the organization the session is currently scoped to.
   * @return The stored organization identifier, or null if none is stored or it cannot be parsed.
   */
  public get currentOrgId(): number | null {
    const currentOrgId = localStorage.getItem(this.currentOrgIdIndex);
    if (!currentOrgId) {
      return null;
    }

    try {
      return JSON.parse(currentOrgId) as number;
    } catch {
      return null;
    }
  }

  /**
   * Stores the functionalities the user's role grants in the current organization.
   * @param functionalities Granted functionalities.
   */
  public set functionalities(functionalities: IExposedFunctionality[]) {
    localStorage.setItem(this.functionalitiesIndex, JSON.stringify(functionalities));
    this.cachedFunctionalities = functionalities ?? null;
  }

  /**
   * Retrieves the functionalities the user's role grants in the current organization, reading
   * them from localStorage only the first time.
   * @return The stored functionalities, or null if none are stored or they cannot be parsed.
   */
  public get functionalities(): IExposedFunctionality[] | null {
    if (this.cachedFunctionalities !== undefined) {
      return this.cachedFunctionalities;
    }

    const functionalities = localStorage.getItem(this.functionalitiesIndex);
    try {
      this.cachedFunctionalities = functionalities ? (JSON.parse(functionalities) as IExposedFunctionality[]) : null;
    } catch {
      this.cachedFunctionalities = null;
    }
    return this.cachedFunctionalities;
  }

  /**
   * Stores the API routes the user's role grants in the current organization.
   * @param routes Granted routes.
   */
  public set routes(routes: IExposedRoute[]) {
    localStorage.setItem(this.routesIndex, JSON.stringify(routes));
    this.cachedRoutes = routes ?? null;
  }

  /**
   * Retrieves the API routes the user's role grants in the current organization, reading them
   * from localStorage only the first time.
   * @return The stored routes, or null if none are stored or they cannot be parsed.
   */
  public get routes(): IExposedRoute[] | null {
    if (this.cachedRoutes !== undefined) {
      return this.cachedRoutes;
    }

    const routes = localStorage.getItem(this.routesIndex);
    try {
      this.cachedRoutes = routes ? (JSON.parse(routes) as IExposedRoute[]) : null;
    } catch {
      this.cachedRoutes = null;
    }
    return this.cachedRoutes;
  }

  /**
   * Stores the user's preferred language.
   * @param lang Language code to store.
   */
  public set userLang(lang: string) {
    localStorage.setItem(this.langIndex, lang);
  }

  /**
   * Retrieves the user's preferred language.
   * @return The stored language code, or the default language if none is stored.
   */
  public get userLang(): string {
    const lang = localStorage.getItem(this.langIndex);
    return lang ? lang : this.DEFAULT_LANG;
  }
}
